/** @jsx isolateComponent */

import { css } from '@emotion/react';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import {
  Accordion,
  Heading,
  HeadingLevel,
  Button,
  Colors,
  Typography,
} from '../../../index';

type FAQItem = {
  title: string;
  content: React.ReactNode;
  href?: string;
  isInitiallyOpen?: boolean;
  isOpen?: boolean;
  onToggle?: ({ isOpen }: { isOpen: boolean }) => void;
};

export type Props = {
  /**
   * An array of FAQ items (accordions) to render
   */
  items?: FAQItem[];

  /**
   * Optional content to render in place of `items`
   */
  children?: React.ReactNode;

  /**
   * Optional override for section heading
   */
  heading?: string;

  /**
   * Text for optional call-to-action button
   */
  ctaText?: string;

  /**
   * URL for optional call-to-action button
   */
  ctaHref?: string;

  /**
   * Click handler for optional call-to-action button
   */
  onCtaClick?: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
};

const Styles = labelStyles('FAQ', {
  root: css`
    padding: 30px 20px;

    @media screen and (min-width: 650px) {
      padding: 50px 30px;
    }
  `,
  faqContent: css`
    max-width: 1170px;
    margin: 0 auto;
  `,
  heading: css`
    text-align: center;
    margin-bottom: 30px;
    color: ${Colors.neutral800};
    ${Typography.pageHeader5}
  `,
  ctaContainer: css`
    text-align: center;
    margin-top: 30px;

    & > a,
    & > button {
      width: 100%;
    }

    @media screen and (min-width: 650px) {
      margin-top: 30px;
      & > a,
      & > button {
        width: auto;
      }
    }
  `,
  grid: css`
    display: grid;
    gap: 20px;

    @media screen and (min-width: 650px) {
      gap: 15px;
    }
  `,
});

export const FAQ = ({
  heading = 'Frequently Asked Questions',
  items,
  children,
  ctaText,
  ctaHref,
  onCtaClick,
}: Props) => {
  return (
    <div css={Styles.root}>
      <div css={Styles.faqContent}>
        <HeadingLevel>
          <Heading css={Styles.heading}>{heading}</Heading>
        </HeadingLevel>
        {items && items.length > 0 ? (
          <div css={Styles.grid}>
            {items.map((item) => (
              <Accordion
                key={item.title}
                title={item.title}
                href={item.href}
                isInitiallyOpen={item.isInitiallyOpen}
                isOpen={item.isOpen}
                onToggle={item.onToggle}
              >
                {item.content}
              </Accordion>
            ))}
          </div>
        ) : (
          children
        )}
        {ctaText && (ctaHref || onCtaClick) && (
          <div css={Styles.ctaContainer}>
            <Button href={ctaHref} size="small" onClick={onCtaClick}>
              {ctaText}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
